import "core-js/modules/es.array.push.js";
/* eslint-disable no-undef */
import * as THREE from 'three';
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { useConfigurationStore } from '@/stores/configurationModule';
import { MATERIAL_THICKNESS } from '@/box/constants.js';
import { createElementFromHTML, normalizeValueInRange, calculatePercentage } from '@/utils/common';
import useShippingBoxModel from './useShippingBoxModel';
import useMailingBoxTLModel from './useMailingBoxTLModel';
import useMailingBoxTIModel from './useMailingBoxTIModel';
import usePizzaBoxModel from './usePizzaBoxModel';
export default function useModel(box, lidTimeline, textureGlobals, scene, textures) {
  const configurationStore = useConfigurationStore();
  const {
    // offscreenCanvasIds,
    hasInsidePrinting,
    insideMaterialColor
  } = storeToRefs(configurationStore);

  // const offscreenCanvasOutside = document.querySelector(`#${offscreenCanvasIds.value[0]}`);
  const offscreenCanvasOutside = document.querySelector(`.lower-canvas-outside`);
  const outsideTextureAtlas = new THREE.CanvasTexture(offscreenCanvasOutside);
  outsideTextureAtlas.colorSpace = THREE.SRGBColorSpace;
  outsideTextureAtlas.magFilter = THREE.LinearFilter;
  outsideTextureAtlas.minFilter = THREE.LinearFilter;

  // const offscreenCanvasInside = document.querySelector(`#${offscreenCanvasIds.value[1]}`);
  const offscreenCanvasInside = document.querySelector(`.lower-canvas-inside`);
  const insideTextureAtlas = new THREE.CanvasTexture(offscreenCanvasInside);
  insideTextureAtlas.colorSpace = THREE.SRGBColorSpace;
  insideTextureAtlas.magFilter = THREE.LinearFilter;
  insideTextureAtlas.minFilter = THREE.LinearFilter;

  // textures.value = [outsideTextureAtlas, insideTextureAtlas];
  textures.value = [outsideTextureAtlas];
  if (hasInsidePrinting.value) {
    textures.value.push(insideTextureAtlas);
  }

  // Create main group
  const group = new THREE.Group();

  // Get the Box SVG
  const svgElement = createElementFromHTML(box.getGeometrySvg());
  const svgContent = svgElement.outerHTML;

  // Load SVG and create paths
  const loader = new SVGLoader();
  const data = loader.parse(svgContent);
  const paths = data.paths;

  // Create meshs from paths
  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    const panelName = path.userData.node.getAttribute('data-panelname');
    const panel = box.panels.find(p => p.name === panelName.split(' ')[0]);

    // Convert the SVG path to a shape
    const shape = path.toShapes(true)[0];

    // @TODO move this
    const extrudeSettings = {
      depth: MATERIAL_THICKNESS,
      bevelEnabled: false,
      curveSegments: 10
    };

    // Create geometry
    const extGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const uvs = extGeo.attributes.uv.array;
    const groups = [
    // Front face group
    {
      start: 0,
      count: extGeo.groups[0].count / 2,
      materialIndex: 0
    },
    // Back face group
    {
      start: extGeo.groups[0].count / 2,
      count: extGeo.groups[0].count / 2,
      materialIndex: 1
    },
    // Side faces group
    {
      start: extGeo.groups[0].count / 2 * 2,
      count: extGeo.groups[1].count,
      materialIndex: 2
    }];
    extGeo.groups = groups;
    let uMin = Infinity;
    let uMax = -Infinity;
    let vMin = Infinity;
    let vMax = -Infinity;

    // calc max and min uv values
    for (let j = 0; j < extGeo.groups[0].count * 4; j += 2) {
      const u = uvs[j];
      const v = uvs[j + 1];
      uMin = Math.min(uMin, u);
      uMax = Math.max(uMax, u);
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }

    // Normalize the UV coordinates
    for (let j = 0; j < extGeo.groups[0].count * 4; j += 2) {
      const u = uvs[j];
      const v = uvs[j + 1];
      uvs[j] = j < extGeo.groups[0].count * 2 ? normalizeValueInRange(u, uMin, uMax) : 1 - normalizeValueInRange(u, uMin, uMax);
      uvs[j + 1] = j < extGeo.groups[0].count * 2 ? 1 - normalizeValueInRange(v, vMin, vMax) : normalizeValueInRange(v, vMin, vMax);
    }
    calculateAtlasRelativeUVs(extGeo, panel, offscreenCanvasOutside, panel.leftPx, panel.topPx, panel.widthPx, panel.heightPx);
    const USE_WIREFRAME = false;
    // const USE_WIREFRAME = true;
    const insideTexture = hasInsidePrinting.value ? insideTextureAtlas : null;
    const _insideMaterialColor = hasInsidePrinting.value ? 0xFFFFFF : insideMaterialColor.value;
    const materials = [
    // Material for front face
    new THREE.MeshStandardMaterial({
      wireframe: USE_WIREFRAME,
      roughness: 0.6,
      map: panel.print ? outsideTextureAtlas : null
    }),
    // Material for back face
    new THREE.MeshStandardMaterial({
      wireframe: USE_WIREFRAME,
      roughness: 0.6,
      map: panel.printInside ? insideTexture : null,
      color: _insideMaterialColor
    }),
    // Material for side faces
    new THREE.MeshStandardMaterial({
      wireframe: USE_WIREFRAME,
      roughness: 0.6,
      color: 0xBE9D67
    })];

    // Create a mesh from the geometry and materials
    const extMesh = new THREE.Mesh(extGeo, materials);
    extMesh.panelName = panelName;
    extMesh.svgPosX = Number(path.userData.node.getAttribute('x'));
    extMesh.svgPosY = Number(path.userData.node.getAttribute('y'));

    // Create a Matrix4 representing the rotation to make it parallel to X-Z plane
    if (box.type !== 'Shipping') {
      var matrix = new THREE.Matrix4().makeRotationX(Math.PI / 2);
      extGeo.applyMatrix4(matrix);
    }

    // Compute the bounding box of the geometry
    extGeo.computeBoundingBox();

    // get the center of the bounding box
    let center = new THREE.Vector3();
    extGeo.boundingBox.getCenter(center);

    // Center the geometry based on the bounding box.
    extGeo.center();

    // reset the position to the original center
    extMesh.position.copy(center);
    // extMesh.updateMatrix();

    group.add(extMesh);
  }
  group.rotation.z = Math.PI;
  group.position.x += box.getDocumentWidth();
  switch (box.type) {
    case 'Shipping':
      useShippingBoxModel(box, group, lidTimeline, scene);
      break;
    case 'Mailer':
      useMailingBoxTLModel(box, group, lidTimeline, scene);
      break;
    case 'Literature':
      useMailingBoxTIModel(box, group, lidTimeline, scene);
      break;
    case 'Pizza':
      usePizzaBoxModel(box, group, lidTimeline, scene);
      break;
  }

  // scene.traverse(p => {
  // if (p.isMesh) {
  //   // if(p.material?.length > 1) {
  //   //   p.material.forEach(m => {
  //   //     m.color = getRandomThreeJSColor();
  //   //   })
  //   // }

  //   // skip if the panelName is not set
  //   if (!p.panelName) return;

  //   // Set inside material color, default to white.
  //   const insideMatColor = hasInsidePrinting.value ? 0xFFFFFF : insideMaterialColor.value;

  //   // Set outside material color, default to white.
  //   const outsideMatColor = hasInsidePrinting.value ? 0xFFFFFF : outsideMaterialColor.value;

  //   const insideMaterialIndex = p.outsideMatIndex === 0 ? 1 : 0;

  //   p.material[insideMaterialIndex].color = new THREE.Color(insideMatColor);
  //   p.material[p.outsideMatIndex].color = new THREE.Color(outsideMatColor);
  // }
  // });

  function calculateAtlasRelativeUVs(geometry, panel, canvas, x, y, width, height) {
    const minU = x / canvas.width;
    const maxU = (x + width) / canvas.width;
    const maxV = y / canvas.height;
    const minV = (y + height) / canvas.height;
    const uvs = geometry.attributes.uv.array;
    let newUVs = [];
    for (let j = 0; j < geometry.groups[0].count * 4; j += 2) {
      const isGroupOne = j < geometry.groups[0].count * 2;
      const _minU = isGroupOne ? minU : 1 - minU;
      const _maxU = isGroupOne ? maxU : 1 - maxU;
      const _minV = 1 - maxV;
      const _maxV = 1 - minV;
      const u = uvs[j];
      const v = isGroupOne ? uvs[j + 1] : 1 - uvs[j + 1];
      newUVs[j] = calculatePercentage(u, _minU, _maxU);
      newUVs[j + 1] = calculatePercentage(v, _minV, _maxV);
    }
    const uvAttribute = new Float32Array(newUVs);
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvAttribute, 2));
    geometry.attributes.uv.needsUpdate = true;
  }

  // @TEMP -
  // get group bounding box and center bounding box
  // set group position center of scene

  // All boxes had to be rotated so i moved the rotation here
  group.rotation.y = Math.PI;
  let bb = new THREE.Box3().setFromObject(group);
  let center = new THREE.Vector3();
  bb.getCenter(center);
  group.position.sub(center);

  // Get bounding box height (y)
  // const bbHeight = bb.max.y - bb.min.y;
  // group.position.y += bbHeight / 2;

  // const boxHelper = new THREE.BoxHelper(group, 0xff00ff); // yellow color for the bounding box
  // scene.add(boxHelper);

  return group;
}